import { api, ApiError } from "../lib/api";
import { getToken } from "../lib/auth";
import { renderHeader } from "../components/header";
import { renderAssessment, type SignalAssessment } from "../components/assessment";
import { attachNoteEditor } from "../lib/note_editor";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SteepleScores {
  social: number;
  technological: number;
  economic: number;
  environmental: number;
  political: number;
  legal: number;
  ethical: number;
  security: number;
}

interface Signal extends SignalAssessment {
  id: string;
  signalRef: string | null;
  title: string;
  source: string;
  sourceUrl: string;
  datePublished: string;
  dateAdded: string;
  workstreams: string[];  // IDs
  steeples: SteepleScores;
  wikilinks: string[];    // names
  summary: string;
  keyImplications: string[];
  rawExcerpts?: string;
  createdAt: string;
  updatedAt: string;
}

interface FolderFile { id: string; name: string; }
interface Folder { date: string; files: FolderFile[]; }
interface Workstream { id: string; name: string; }
interface WikilinkItem { id: string; name: string; signalCount: number; }
interface BacklinkSignal { id: string; name: string; }
interface BacklinkGroup { wikilink: string; count: number; signals: BacklinkSignal[]; }

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

let _leftEl: HTMLElement;
let _centreEl: HTMLElement;
let _rightEl: HTMLElement;
let _currentSignalId: string | null = null;
let _allWorkstreams: Workstream[] = [];
let _allWikilinks: WikilinkItem[] = [];
let _wikilinkFilter: string | null = null;  // name of active wikilink highlight
let _filteredIds: Set<string> = new Set();
// Set by the graph view (via ?signal= query param) to auto-open a signal on load
let _autoSelectSignalId: string | null = null;

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

async function downloadSignalMd(signalId: string): Promise<void> {
  const token = getToken();
  try {
    const res = await fetch(`/api/signals/${signalId}/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `signal-${signalId.slice(0, 8)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    alert(`Download failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}

// ---------------------------------------------------------------------------
// Page entry point
// ---------------------------------------------------------------------------

export function renderFolderView(root: HTMLElement): void {
  // If the graph view navigated here with a ?signal=ID param, auto-select it
  _autoSelectSignalId = new URLSearchParams(location.search).get("signal");

  root.appendChild(renderHeader());

  const layout = document.createElement("div");
  layout.className = "fv-layout";

  _leftEl = document.createElement("aside");
  _leftEl.className = "fv-left";

  _centreEl = document.createElement("main");
  _centreEl.className = "fv-centre";

  _rightEl = document.createElement("aside");
  _rightEl.className = "fv-right";

  layout.appendChild(_leftEl);
  layout.appendChild(_centreEl);
  layout.appendChild(_rightEl);

  // Mobile peek tab — left-edge handle that opens the sidebar as a full-screen overlay
  const peekTab = document.createElement("button");
  peekTab.className = "fv-peek-tab";
  peekTab.setAttribute("aria-label", "Open signal list");
  peekTab.textContent = "Signals";
  peekTab.addEventListener("click", () => _leftEl.classList.add("fv-sidebar-open"));
  layout.appendChild(peekTab);

  root.appendChild(layout);

  // Populate right sidebar header immediately
  _rightEl.innerHTML = `
    <div class="fv-sidebar-header">
      <span class="fv-sidebar-title">Backlinks</span>
    </div>
    <div class="fv-backlinks-body" id="fv-backlinks-body">
      <p class="muted fv-sidebar-placeholder">Select a signal to see backlinks.</p>
    </div>`;

  // Show empty centre
  renderCentreEmpty();

  // Load data
  loadFolders();
  loadWorkstreams();
  loadWikilinks();
}

// ---------------------------------------------------------------------------
// Left sidebar — folder list
// ---------------------------------------------------------------------------

async function loadFolders(): Promise<void> {
  _leftEl.innerHTML = `
    <div class="fv-sidebar-header">
      <span class="fv-sidebar-title">Signals</span>
      <button class="fv-sidebar-close icon-btn" aria-label="Close panel" title="Close">✕</button>
    </div>
    <div id="fv-wl-filter-bar" style="display:none"></div>
    <div id="fv-folder-list" class="fv-folder-list">
      <p class="muted fv-sidebar-placeholder">Loading…</p>
    </div>`;

  _leftEl.querySelector(".fv-sidebar-close")
    ?.addEventListener("click", () => _leftEl.classList.remove("fv-sidebar-open"));

  try {
    const folders = await api<Folder[]>("/folders");
    renderFolderList(folders);
  } catch (_e) {
    const list = _leftEl.querySelector<HTMLDivElement>("#fv-folder-list")!;
    list.innerHTML = `<p class="muted fv-sidebar-placeholder">Failed to load signals.</p>`;
  }
}

function renderFolderList(folders: Folder[]): void {
  const list = _leftEl.querySelector<HTMLDivElement>("#fv-folder-list")!;
  if (!folders.length) {
    list.innerHTML = `<p class="muted fv-sidebar-placeholder">No signals yet.</p>`;
    return;
  }
  list.innerHTML = "";
  folders.forEach((folder, idx) => {
    const section = document.createElement("div");
    section.className = "fv-folder";

    const isOpen = idx === 0; // first (newest) month open by default
    section.innerHTML = `
      <button class="fv-folder-header" aria-expanded="${isOpen}">
        <span class="fv-folder-arrow">${isOpen ? "▾" : "▸"}</span>
        <span class="fv-folder-label">${escHtml(folder.date)}</span>
        <span class="fv-folder-count muted">${folder.files.length}</span>
      </button>
      <div class="fv-folder-files" style="${isOpen ? "" : "display:none"}">
        ${folder.files.map((f) => `
          <button class="fv-file-btn" data-signal-id="${f.id}" title="${escHtml(f.name)}">
            ${escHtml(f.name)}
          </button>`).join("")}
      </div>`;

    const headerBtn = section.querySelector<HTMLButtonElement>(".fv-folder-header")!;
    const filesDiv = section.querySelector<HTMLDivElement>(".fv-folder-files")!;
    const arrow = section.querySelector<HTMLSpanElement>(".fv-folder-arrow")!;

    headerBtn.addEventListener("click", () => {
      const open = headerBtn.getAttribute("aria-expanded") === "true";
      headerBtn.setAttribute("aria-expanded", String(!open));
      filesDiv.style.display = open ? "none" : "";
      arrow.textContent = open ? "▸" : "▾";
    });

    filesDiv.querySelectorAll<HTMLButtonElement>(".fv-file-btn").forEach((btn) => {
      btn.addEventListener("click", () => selectSignal(btn.dataset.signalId!));
    });

    list.appendChild(section);
  });

  // Apply wikilink highlight if filter active
  applyWikilinkHighlight();

  // Auto-select signal navigated here from Graph View
  if (_autoSelectSignalId) {
    const targetId = _autoSelectSignalId;
    _autoSelectSignalId = null;
    const btn = list.querySelector<HTMLButtonElement>(`[data-signal-id="${targetId}"]`);
    if (btn) {
      // Expand the folder containing this signal if collapsed
      const filesDiv = btn.closest<HTMLDivElement>(".fv-folder-files");
      if (filesDiv && filesDiv.style.display === "none") {
        filesDiv.style.display = "";
        const headerBtn = filesDiv.previousElementSibling as HTMLButtonElement | null;
        if (headerBtn) {
          headerBtn.setAttribute("aria-expanded", "true");
          const arrow = headerBtn.querySelector<HTMLSpanElement>(".fv-folder-arrow");
          if (arrow) arrow.textContent = "▾";
        }
      }
      btn.scrollIntoView({ block: "nearest" });
      void selectSignal(targetId);
    }
  }
}

function applyWikilinkHighlight(): void {
  const btns = _leftEl.querySelectorAll<HTMLButtonElement>(".fv-file-btn");
  btns.forEach((btn) => {
    if (_wikilinkFilter) {
      btn.classList.toggle("wl-match", _filteredIds.has(btn.dataset.signalId!));
    } else {
      btn.classList.remove("wl-match");
    }
  });

  const bar = _leftEl.querySelector<HTMLDivElement>("#fv-wl-filter-bar")!;
  if (_wikilinkFilter) {
    bar.style.display = "";
    bar.innerHTML = `
      <div class="fv-wl-filter-active">
        <span class="wikilink-chip active">#${escHtml(_wikilinkFilter)}</span>
        <span class="muted">${_filteredIds.size} signal(s)</span>
        <button class="icon-btn fv-wl-clear" title="Clear filter">✕</button>
      </div>`;
    bar.querySelector(".fv-wl-clear")!.addEventListener("click", clearWikilinkFilter);
  } else {
    bar.style.display = "none";
    bar.innerHTML = "";
  }
}

async function activateWikilinkFilter(name: string): Promise<void> {
  if (_wikilinkFilter === name) {
    clearWikilinkFilter();
    return;
  }
  try {
    const sigs = await api<Signal[]>(`/signals?wikilink=${encodeURIComponent(name)}`);
    _filteredIds = new Set(sigs.map((s) => s.id));
    _wikilinkFilter = name;
    applyWikilinkHighlight();
  } catch (_e) {
    // ignore
  }
}

function clearWikilinkFilter(): void {
  _wikilinkFilter = null;
  _filteredIds.clear();
  applyWikilinkHighlight();
}

function markActiveFile(signalId: string): void {
  _leftEl.querySelectorAll<HTMLButtonElement>(".fv-file-btn").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.signalId === signalId);
  });
}

// ---------------------------------------------------------------------------
// Signal selection
// ---------------------------------------------------------------------------

async function selectSignal(signalId: string): Promise<void> {
  _currentSignalId = signalId;
  _leftEl.classList.remove("fv-sidebar-open"); // close overlay on mobile when a signal is picked
  markActiveFile(signalId);
  _centreEl.innerHTML = `<div class="fv-loading muted">Loading…</div>`;
  const backlinksBody = document.getElementById("fv-backlinks-body");
  if (backlinksBody) backlinksBody.innerHTML = `<p class="muted fv-sidebar-placeholder">Loading…</p>`;

  try {
    const [sig, backlinks] = await Promise.all([
      api<Signal>(`/signals/${signalId}`),
      api<BacklinkGroup[]>(`/signals/${signalId}/backlinks`),
    ]);
    renderCentreSignal(sig);
    renderBacklinks(backlinks);
  } catch (_e) {
    _centreEl.innerHTML = `<div class="fv-loading muted">Failed to load signal.</div>`;
  }
}

// ---------------------------------------------------------------------------
// Centre panel
// ---------------------------------------------------------------------------

function renderCentreEmpty(): void {
  _centreEl.innerHTML = `
    <div class="fv-empty">
      <div class="fv-empty-icon">📄</div>
      <p class="muted">Select a signal to read</p>
    </div>`;
}

const STEEPLES_LABELS: [keyof SteepleScores, string][] = [
  ["social", "Social"],
  ["technological", "Technological"],
  ["economic", "Economic"],
  ["environmental", "Environmental"],
  ["political", "Political"],
  ["legal", "Legal"],
  ["ethical", "Ethical"],
  ["security", "Security"],
];

function renderCentreSignal(sig: Signal): void {
  // Workstream pills
  const wsPills = sig.workstreams
    .map((id) => {
      const ws = _allWorkstreams.find((w) => w.id === id);
      return ws
        ? `<span class="ws-pill">${escHtml(ws.name)}</span>`
        : "";
    })
    .join("");

  // STEEPLES grid
  const steeplesBadges = STEEPLES_LABELS.map(([key, label]) => {
    const score = sig.steeples[key];
    const cls = score >= 6 ? "steeples-badge high" : "steeples-badge";
    return `<div class="${cls}"><span class="steeples-label">${label}</span><span class="steeples-score">${score}/10</span></div>`;
  }).join("");

  // Key implications
  const implList = sig.keyImplications
    .map((i) => `<li>${escHtml(i)}</li>`)
    .join("");

  // Wikilink chips
  const wlChips = sig.wikilinks
    .map((name) => `<button class="wikilink-chip" data-wl="${escHtml(name)}">#${escHtml(name)}</button>`)
    .join("");

  // The v6 assessment — type, claim status, horizon and its reason, story,
  // novelty. Shared with the `/signals/:ref` permalink rather than written
  // twice: two renderers of one signal that disagree about it is a worse bug
  // than either of them being slightly wrong.
  const assessment = renderAssessment(sig, sig.datePublished);

  // Raw excerpts
  const excerptSection = sig.rawExcerpts
    ? `<details class="fv-excerpts">
        <summary class="muted">Raw Excerpts</summary>
        <pre class="fv-excerpts-body mono">${escHtml(sig.rawExcerpts)}</pre>
       </details>`
    : "";

  _centreEl.innerHTML = `
    <div class="fv-detail">
      <div class="fv-detail-header">
        <h2 class="fv-title serif">${escHtml(sig.title)}</h2>
        <div class="fv-meta mono">
          <a href="${escHtml(sig.sourceUrl)}" target="_blank" rel="noopener">${escHtml(sig.source)}</a>
          <span>Published: ${escHtml(sig.datePublished)}</span>
          <span class="muted">Added: ${escHtml(sig.dateAdded)}</span>
          ${sig.signalRef ? `<a href="/signals/${escHtml(sig.signalRef)}" data-route
            class="fv-ref" title="Permalink to this signal">${escHtml(sig.signalRef)}</a>` : ""}
        </div>
        <div class="fv-ws-row">
          ${wsPills}
          <button class="fv-ws-add-btn" id="fv-ws-btn" title="Manage workstreams">+ Workstreams</button>
        </div>
        <div class="fv-detail-actions">
          <button class="primary fv-edit-btn" id="fv-edit-btn">Edit</button>
          <button class="fv-delete-btn" id="fv-delete-btn">Delete</button>
          <button class="fv-export-link" id="fv-export-btn">Download .md</button>
        </div>
      </div>

      <hr class="fv-divider">
${assessment ? `
      <div class="fv-assessment">${assessment}</div>

      <hr class="fv-divider">
` : ""}
      <div class="fv-steeples-grid">
        ${steeplesBadges}
      </div>

      <hr class="fv-divider">

      <div class="fv-summary serif">${escHtml(sig.summary)}</div>

      <ul class="fv-implications">
        ${implList}
      </ul>

      <div class="fv-wikilinks">
        ${wlChips}
      </div>

      ${excerptSection}

      <div id="fv-note-editor"></div>
    </div>`;

  // Wire up wikilink chip clicks
  _centreEl.querySelectorAll<HTMLButtonElement>(".wikilink-chip").forEach((chip) => {
    chip.addEventListener("click", () => activateWikilinkFilter(chip.dataset.wl!));
  });

  // Wire up workstream popover
  document.getElementById("fv-ws-btn")?.addEventListener("click", (e) => {
    openWorkstreamPopover(sig, e.currentTarget as HTMLElement);
  });

  // Wire up edit
  document.getElementById("fv-edit-btn")?.addEventListener("click", () => {
    openSignalEditModal(sig);
  });

  // Wire up delete
  document.getElementById("fv-delete-btn")?.addEventListener("click", () => {
    openDeleteModal(sig);
  });

  // Wire up export
  document.getElementById("fv-export-btn")?.addEventListener("click", () => {
    downloadSignalMd(sig.id);
  });

  // Mount comment thread
  const noteContainer = _centreEl.querySelector<HTMLDivElement>("#fv-note-editor")!;
  attachNoteEditor(sig.id, noteContainer);

  // Highlight active chip if filter is on
  if (_wikilinkFilter) {
    _centreEl.querySelectorAll<HTMLButtonElement>(".wikilink-chip").forEach((chip) => {
      if (chip.dataset.wl === _wikilinkFilter) chip.classList.add("active");
    });
  }
}

// ---------------------------------------------------------------------------
// Right sidebar — backlinks
// ---------------------------------------------------------------------------

function renderBacklinks(groups: BacklinkGroup[]): void {
  const body = document.getElementById("fv-backlinks-body");
  if (!body) return;
  if (!groups.length) {
    body.innerHTML = `<p class="muted fv-sidebar-placeholder">No backlinks yet.</p>`;
    return;
  }
  body.innerHTML = groups
    .map(
      (g) => `
    <div class="fv-backlink-group">
      <div class="fv-backlink-header">
        <span class="wikilink-chip">#${escHtml(g.wikilink)}</span>
        <span class="muted fv-backlink-count">${g.count}</span>
      </div>
      <ul class="fv-backlink-list">
        ${g.signals
          .map(
            (s) =>
              `<li><button class="fv-backlink-file" data-signal-id="${s.id}" title="${escHtml(s.name)}">${escHtml(s.name)}</button></li>`
          )
          .join("")}
      </ul>
    </div>`
    )
    .join("");

  body.querySelectorAll<HTMLButtonElement>(".fv-backlink-file").forEach((btn) => {
    btn.addEventListener("click", () => selectSignal(btn.dataset.signalId!));
  });
}

// ---------------------------------------------------------------------------
// Workstream & Wikilink data loaders
// ---------------------------------------------------------------------------

async function loadWorkstreams(): Promise<void> {
  try {
    _allWorkstreams = await api<Workstream[]>("/workstreams");
  } catch (_e) {
    _allWorkstreams = [];
  }
}

async function loadWikilinks(): Promise<void> {
  try {
    _allWikilinks = await api<WikilinkItem[]>("/wikilinks");
  } catch (_e) {
    _allWikilinks = [];
  }
}

// ---------------------------------------------------------------------------
// Workstream Popover — inline workstream assignment (PRD §6.5)
// ---------------------------------------------------------------------------

function openWorkstreamPopover(sig: Signal, anchor: HTMLElement): void {
  // Remove any existing popover
  document.querySelectorAll(".ws-popover").forEach((el) => el.remove());

  const popover = document.createElement("div");
  popover.className = "ws-popover";

  if (!_allWorkstreams.length) {
    popover.innerHTML = `<p class="muted" style="padding:8px 12px;font-size:13px;">No workstreams yet.<br>
      <a href="#" id="ws-create-link">Create one</a></p>`;
    popover.querySelector("#ws-create-link")?.addEventListener("click", (e) => {
      e.preventDefault();
      popover.remove();
      openCreateWorkstreamModal();
    });
  } else {
    popover.innerHTML = `
      <div class="ws-popover-header">
        <span style="font-size:12px;font-weight:600;">Workstreams</span>
        <button class="icon-btn ws-create-btn" title="New workstream">+</button>
      </div>
      ${_allWorkstreams.map((ws) => `
        <label class="ws-popover-item">
          <input type="checkbox" value="${ws.id}" ${sig.workstreams.includes(ws.id) ? "checked" : ""}>
          <span>${escHtml(ws.name)}</span>
        </label>`).join("")}`;

    // Save immediately on change
    popover.querySelectorAll<HTMLInputElement>("input[type=checkbox]").forEach((cb) => {
      cb.addEventListener("change", async () => {
        const ids = Array.from(popover.querySelectorAll<HTMLInputElement>("input:checked")).map((c) => c.value);
        try {
          const updated = await api<Signal>(`/signals/${sig.id}/workstreams`, {
            method: "PATCH",
            body: { workstream_ids: ids },
          });
          // Update sig in-place so re-renders see fresh data
          sig.workstreams = updated.workstreams;
          refreshWorkstreamPills(updated);
        } catch (_e) {
          cb.checked = !cb.checked; // revert on error
        }
      });
    });

    popover.querySelector(".ws-create-btn")?.addEventListener("click", () => {
      popover.remove();
      openCreateWorkstreamModal();
    });
  }

  // Position below anchor
  const rect = anchor.getBoundingClientRect();
  popover.style.top = `${rect.bottom + window.scrollY + 4}px`;
  popover.style.left = `${rect.left + window.scrollX}px`;
  document.body.appendChild(popover);

  // Close on outside click
  function onOutside(e: MouseEvent): void {
    if (!popover.contains(e.target as Node) && e.target !== anchor) {
      popover.remove();
      document.removeEventListener("mousedown", onOutside);
    }
  }
  setTimeout(() => document.addEventListener("mousedown", onOutside), 0);
}

function refreshWorkstreamPills(sig: Signal): void {
  const row = _centreEl.querySelector<HTMLDivElement>(".fv-ws-row");
  if (!row) return;
  const addBtn = row.querySelector<HTMLButtonElement>("#fv-ws-btn");
  const wsPills = sig.workstreams
    .map((id) => {
      const ws = _allWorkstreams.find((w) => w.id === id);
      return ws ? `<span class="ws-pill">${escHtml(ws.name)}</span>` : "";
    })
    .join("");
  row.innerHTML = wsPills;
  if (addBtn) {
    row.appendChild(addBtn);
  } else {
    const btn = document.createElement("button");
    btn.className = "fv-ws-add-btn";
    btn.id = "fv-ws-btn";
    btn.title = "Manage workstreams";
    btn.textContent = "+ Workstreams";
    btn.addEventListener("click", (e) => openWorkstreamPopover(sig, e.currentTarget as HTMLElement));
    row.appendChild(btn);
  }
}

// ---------------------------------------------------------------------------
// Create Workstream Modal (quick-create from within the popover)
// ---------------------------------------------------------------------------

function openCreateWorkstreamModal(): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal-box" style="max-width:380px">
      <div class="modal-header">
        <h3>New Workstream</h3>
        <button class="icon-btn modal-close">✕</button>
      </div>
      <div class="modal-body">
        <div class="field-group">
          <label>Name</label>
          <input type="text" id="cws-name" placeholder="e.g. AI Regulation Review" />
        </div>
        <p class="modal-err muted" id="cws-err"></p>
      </div>
      <div class="modal-footer">
        <button class="primary" id="cws-save">Create</button>
        <button id="cws-cancel">Cancel</button>
      </div>
    </div>`;

  const close = (): void => { overlay.remove(); };
  overlay.querySelector(".modal-close")!.addEventListener("click", close);
  overlay.querySelector("#cws-cancel")!.addEventListener("click", close);
  overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(); });

  overlay.querySelector("#cws-save")!.addEventListener("click", async () => {
    const name = (overlay.querySelector<HTMLInputElement>("#cws-name")!).value.trim();
    const errEl = overlay.querySelector<HTMLParagraphElement>("#cws-err")!;
    if (!name) { errEl.textContent = "Name is required."; return; }
    try {
      const ws = await api<Workstream>("/workstreams", { method: "POST", body: { name } });
      _allWorkstreams.push(ws);
      _allWorkstreams.sort((a, b) => a.name.localeCompare(b.name));
      close();
    } catch (err) {
      errEl.textContent = err instanceof ApiError ? err.message : "Failed to create workstream.";
    }
  });

  document.body.appendChild(overlay);
  overlay.querySelector<HTMLInputElement>("#cws-name")?.focus();
}

// ---------------------------------------------------------------------------
// Delete Confirmation Modal
// ---------------------------------------------------------------------------

function openDeleteModal(sig: Signal): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal-box" style="max-width:420px">
      <div class="modal-header">
        <h3>Delete Signal?</h3>
        <button class="icon-btn modal-close">✕</button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to delete <strong>${escHtml(sig.title)}</strong>?</p>
        <p class="muted" style="font-size:13px;">This action cannot be undone.</p>
        <p class="modal-err muted" id="del-err"></p>
      </div>
      <div class="modal-footer">
        <button class="primary destructive" id="del-confirm">Delete</button>
        <button id="del-cancel">Cancel</button>
      </div>
    </div>`;

  const close = (): void => { overlay.remove(); };
  overlay.querySelector(".modal-close")!.addEventListener("click", close);
  overlay.querySelector("#del-cancel")!.addEventListener("click", close);
  overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(); });

  overlay.querySelector("#del-confirm")!.addEventListener("click", async () => {
    const errEl = overlay.querySelector<HTMLParagraphElement>("#del-err")!;
    try {
      await api(`/signals/${sig.id}`, { method: "DELETE" });
      close();
      renderCentreEmpty();
      _currentSignalId = null;
      const backlinksBody = document.getElementById("fv-backlinks-body");
      if (backlinksBody) backlinksBody.innerHTML = `<p class="muted fv-sidebar-placeholder">Select a signal to see backlinks.</p>`;
      // Reload the folder list
      await loadFolders();
    } catch (err) {
      errEl.textContent = err instanceof ApiError ? err.message : "Delete failed.";
    }
  });

  document.body.appendChild(overlay);
}

// ---------------------------------------------------------------------------
// Signal Edit Modal — all fields including wikilink autocomplete
// ---------------------------------------------------------------------------

function openSignalEditModal(sig: Signal): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";

  // Build workstream checkboxes
  const wsChecks = _allWorkstreams.map((ws) => `
    <label class="ws-check-item">
      <input type="checkbox" name="ws" value="${ws.id}" ${sig.workstreams.includes(ws.id) ? "checked" : ""}>
      <span>${escHtml(ws.name)}</span>
    </label>`).join("") || `<span class="muted" style="font-size:13px;">No workstreams. Create one first.</span>`;

  // Build STEEPLES fields
  const steeplesFields = STEEPLES_LABELS.map(([key, label]) => `
    <div class="steeples-edit-item">
      <label>${label}</label>
      <input type="number" class="steeples-input" data-key="${key}"
             min="0" max="10" value="${sig.steeples[key]}" />
    </div>`).join("");

  // Build key implications list
  const implItems = sig.keyImplications.map((imp, i) => `
    <div class="impl-item" data-idx="${i}">
      <input type="text" class="impl-input" value="${escHtml(imp)}" />
      <button class="icon-btn impl-remove" title="Remove">✕</button>
    </div>`).join("");

  overlay.innerHTML = `
    <div class="modal-box modal-large">
      <div class="modal-header">
        <h3>Edit Signal</h3>
        <button class="icon-btn modal-close">✕</button>
      </div>
      <div class="modal-body">

        <div class="field-group">
          <label>Title</label>
          <input type="text" id="em-title" value="${escHtml(sig.title)}" />
        </div>

        <div class="field-row">
          <div class="field-group">
            <label>Source</label>
            <input type="text" id="em-source" value="${escHtml(sig.source)}" />
          </div>
          <div class="field-group">
            <label>Date Published</label>
            <input type="date" id="em-date" value="${escHtml(sig.datePublished)}" />
          </div>
        </div>

        <div class="field-group">
          <label>Source URL</label>
          <input type="url" id="em-url" value="${escHtml(sig.sourceUrl)}" />
        </div>

        <div class="field-group">
          <label>Workstreams</label>
          <div class="ws-checks">${wsChecks}</div>
        </div>

        <div class="field-group">
          <label>Wikilinks <span class="muted" style="font-weight:400;">(lowercase, hyphenated)</span></label>
          <div class="tag-input-wrapper">
            <div class="tag-chips" id="em-wl-chips">
              ${sig.wikilinks.map((n) => `
                <span class="tag-chip" data-wl="${escHtml(n)}">#${escHtml(n)}
                  <button class="tag-chip-remove" data-wl="${escHtml(n)}">✕</button>
                </span>`).join("")}
            </div>
            <input type="text" id="em-wl-input" placeholder="Type to search or create…" autocomplete="off" />
            <ul class="tag-suggestions hidden" id="em-wl-suggestions"></ul>
          </div>
        </div>

        <div class="field-group">
          <label>STEEPLES Scores</label>
          <div class="steeples-edit-grid">${steeplesFields}</div>
        </div>

        <div class="field-group">
          <label>Summary</label>
          <textarea id="em-summary" rows="4">${escHtml(sig.summary)}</textarea>
        </div>

        <div class="field-group">
          <label>Key Implications</label>
          <div class="impl-list" id="em-implications">${implItems}</div>
          <button class="impl-add-btn" id="em-impl-add">+ Add implication</button>
        </div>

        <div class="field-group">
          <label>Raw Excerpts <span class="muted" style="font-weight:400;">(optional)</span></label>
          <textarea id="em-raw" rows="4">${escHtml(sig.rawExcerpts ?? "")}</textarea>
        </div>

        <p class="modal-err muted" id="em-err"></p>
      </div>

      <div class="modal-footer">
        <button class="primary" id="em-save">Save</button>
        <button id="em-cancel">Cancel</button>
      </div>
    </div>`;

  const close = (): void => { overlay.remove(); };
  overlay.querySelector(".modal-close")!.addEventListener("click", close);
  overlay.querySelector("#em-cancel")!.addEventListener("click", close);
  overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(); });

  // --- Wikilink tag input logic ---
  const wlInput = overlay.querySelector<HTMLInputElement>("#em-wl-input")!;
  const wlChips = overlay.querySelector<HTMLDivElement>("#em-wl-chips")!;
  const wlSuggestions = overlay.querySelector<HTMLUListElement>("#em-wl-suggestions")!;
  let currentWikilinks: string[] = [...sig.wikilinks];

  function renderChips(): void {
    wlChips.innerHTML = currentWikilinks.map((n) => `
      <span class="tag-chip" data-wl="${escHtml(n)}">#${escHtml(n)}
        <button class="tag-chip-remove" data-wl="${escHtml(n)}">✕</button>
      </span>`).join("");
    wlChips.querySelectorAll<HTMLButtonElement>(".tag-chip-remove").forEach((btn) => {
      btn.addEventListener("click", () => {
        currentWikilinks = currentWikilinks.filter((n) => n !== btn.dataset.wl);
        renderChips();
      });
    });
  }
  renderChips();

  function addWikilink(name: string): void {
    const n = name.trim().toLowerCase().replace(/\s+/g, "-");
    if (n && !currentWikilinks.includes(n)) {
      currentWikilinks.push(n);
      renderChips();
    }
    wlInput.value = "";
    wlSuggestions.classList.add("hidden");
    wlSuggestions.innerHTML = "";
  }

  wlInput.addEventListener("input", () => {
    const q = wlInput.value.trim().toLowerCase();
    if (!q) { wlSuggestions.classList.add("hidden"); return; }
    const matches = _allWikilinks
      .filter((wl) => wl.name.includes(q) && !currentWikilinks.includes(wl.name))
      .slice(0, 8);
    if (!matches.length) { wlSuggestions.classList.add("hidden"); return; }
    wlSuggestions.innerHTML = matches.map((wl) =>
      `<li class="tag-suggestion-item" data-wl="${escHtml(wl.name)}">#${escHtml(wl.name)} <span class="muted">${wl.signalCount}</span></li>`
    ).join("");
    wlSuggestions.classList.remove("hidden");
    wlSuggestions.querySelectorAll<HTMLLIElement>(".tag-suggestion-item").forEach((li) => {
      li.addEventListener("mousedown", (e) => {
        e.preventDefault();
        addWikilink(li.dataset.wl!);
      });
    });
  });

  wlInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); addWikilink(wlInput.value); }
    if (e.key === "Escape") { wlSuggestions.classList.add("hidden"); }
  });

  wlInput.addEventListener("blur", () => {
    setTimeout(() => wlSuggestions.classList.add("hidden"), 150);
  });

  // --- Key implications dynamic list ---
  const implList = overlay.querySelector<HTMLDivElement>("#em-implications")!;

  function wireImplButtons(): void {
    implList.querySelectorAll<HTMLButtonElement>(".impl-remove").forEach((btn) => {
      btn.addEventListener("click", () => {
        btn.closest(".impl-item")?.remove();
      });
    });
  }
  wireImplButtons();

  overlay.querySelector("#em-impl-add")!.addEventListener("click", () => {
    const item = document.createElement("div");
    item.className = "impl-item";
    item.innerHTML = `<input type="text" class="impl-input" placeholder="Implication…" />
      <button class="icon-btn impl-remove" title="Remove">✕</button>`;
    item.querySelector(".impl-remove")!.addEventListener("click", () => item.remove());
    implList.appendChild(item);
    item.querySelector<HTMLInputElement>(".impl-input")!.focus();
  });

  // --- Save ---
  overlay.querySelector("#em-save")!.addEventListener("click", async () => {
    const errEl = overlay.querySelector<HTMLParagraphElement>("#em-err")!;
    const title = overlay.querySelector<HTMLInputElement>("#em-title")!.value.trim();
    const source = overlay.querySelector<HTMLInputElement>("#em-source")!.value.trim();
    const sourceUrl = overlay.querySelector<HTMLInputElement>("#em-url")!.value.trim();
    const datePublished = overlay.querySelector<HTMLInputElement>("#em-date")!.value.trim();
    const summary = overlay.querySelector<HTMLTextAreaElement>("#em-summary")!.value.trim();
    const rawExcerpts = overlay.querySelector<HTMLTextAreaElement>("#em-raw")!.value.trim() || undefined;

    if (!title || !source || !datePublished || !summary) {
      errEl.textContent = "Title, Source, Date Published, and Summary are required.";
      return;
    }

    const steeples: Record<string, number> = {};
    overlay.querySelectorAll<HTMLInputElement>(".steeples-input").forEach((inp) => {
      const val = Math.max(0, Math.min(10, Number(inp.value) || 0));
      steeples[inp.dataset.key!] = val;
    });

    const workstreamIds = Array.from(
      overlay.querySelectorAll<HTMLInputElement>("input[name=ws]:checked")
    ).map((cb) => cb.value);

    const keyImplications = Array.from(
      overlay.querySelectorAll<HTMLInputElement>(".impl-input")
    ).map((inp) => inp.value.trim()).filter(Boolean);

    const body = {
      title, source, sourceUrl, datePublished, summary, rawExcerpts,
      steeples,
      wikilinks: currentWikilinks,
      workstreams: workstreamIds,
      keyImplications,
    };

    try {
      const updated = await api<Signal>(`/signals/${sig.id}`, { method: "PATCH", body });
      // Refresh wikilinks list (new ones may have been created)
      loadWikilinks();
      close();
      // Re-render with updated data
      renderCentreSignal(updated);
      // Reload sidebar (title/date might have changed → filename changes)
      await loadFolders();
      if (_currentSignalId) markActiveFile(_currentSignalId);
      // Reload backlinks (wikilinks may have changed)
      const bl = await api<BacklinkGroup[]>(`/signals/${updated.id}/backlinks`);
      renderBacklinks(bl);
    } catch (err) {
      errEl.textContent = err instanceof ApiError ? err.message : "Save failed.";
    }
  });

  document.body.appendChild(overlay);
  overlay.querySelector<HTMLInputElement>("#em-title")?.focus();
}
